<template>
  <div class="searchable-select">
    <input
      type="text"
      :value="value"
      :required="required"
      style="position: absolute; opacity: 0; height: 0; width: 0; pointer-events: none;"
      tabindex="-1"
    />
    <b-button
      :id="`searchable-select-${_uid}`"
      :disabled="disabled"
      :variant="variant"
      :size="size"
      block
      :class="{ 'is-invalid': required && !value }"
    >
      <div class="d-flex justify-content-between align-items-center">
        <span :class="{ 'text-muted': !selectedText }">
          {{ selectedText || placeholder }}
        </span>
        <i class="fa fa-chevron-down ml-2"></i>
      </div>
    </b-button>

    <b-popover
      :target="`searchable-select-${_uid}`"
      triggers="focus"
      placement="bottom"
      :show.sync="showPopover"
      custom-class="searchable-select-popover"
      @shown="onShown"
      @hidden="onHidden"
    >
      <div class="popover-content">
        <b-form-input
          ref="searchInput"
          v-model="searchQuery"
          type="text"
          placeholder="Search..."
          size="sm"
          autocomplete="off"
          class="mb-2"
        />

        <div class="options-container">
          <div
            v-for="option in filteredOptions"
            :key="option[valueField]"
            class="option-item"
            :class="{ active: value === option[valueField] }"
            @click="selectOption(option)"
          >
            {{ option[textField] }}
          </div>

          <div v-if="filteredOptions.length === 0" class="text-muted text-center py-2">
            No results found
          </div>
        </div>
      </div>
    </b-popover>
  </div>
</template>

<script>
export default {
  name: 'SearchableSelect',
  props: {
    value: {
      type: [String, Number, Object],
      default: null,
    },
    options: {
      type: Array,
      required: true,
      default: () => [],
    },
    valueField: {
      type: String,
      default: 'value',
    },
    textField: {
      type: String,
      default: 'text',
    },
    placeholder: {
      type: String,
      default: 'Select option...',
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    variant: {
      type: String,
      default: 'outline-secondary',
    },
    size: {
      type: String,
      default: 'md',
    },
    required: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      searchQuery: '',
      showPopover: false
    }
  },
  computed: {
    filteredOptions() {
      if (!this.searchQuery) {
        return this.options
      }
      const query = this.searchQuery.toLowerCase()
      return this.options.filter((option) =>
        option[this.textField].toLowerCase().includes(query)
      )
    },
    selectedText() {
      if (!this.value) return ''
      const selected = this.options.find(
        (opt) => opt[this.valueField] === this.value
      )
      return selected ? selected[this.textField] : ''
    },
  },
  methods: {
    selectOption(option) {
      this.$emit('input', option[this.valueField])
      this.$emit('change', option[this.valueField])
      this.showPopover = false
      this.searchQuery = ''
    },
    onShown() {
      this.$nextTick(() => {
        if (this.$refs.searchInput) {
          this.$refs.searchInput.focus()
        }
      })
    },
    onHidden() {
      this.searchQuery = ''
    },
  },
}
</script>

<style scoped>
.searchable-select {
  position: relative;
}

/* Button styling to match form-control */
::v-deep .btn {
  text-align: left;
  border: 1px solid #ced4da;
  background-color: white;
  color: #495057;
  padding: 0.375rem 0.75rem;
}

::v-deep .btn.is-invalid,
::v-deep .is-invalid.btn {
  border-color: #dc3545 !important;
  background-color: white;
}

::v-deep .btn.is-invalid:hover,
::v-deep .is-invalid.btn:hover {
  border-color: #dc3545 !important;
}

::v-deep .btn:hover:not(:disabled) {
  background-color: white;
  border-color: #ced4da;
  color: #495057;
}

::v-deep .btn:focus,
::v-deep .btn.focus {
  box-shadow: 0 0 0 0.2rem rgba(0, 123, 255, 0.25);
  border-color: #80bdff;
}

::v-deep .btn:disabled {
  background-color: #e9ecef;
  cursor: not-allowed;
}

/* Popover styling */
::v-deep .searchable-select-popover {
  max-width: 400px;
  z-index: 99999 !important;
}

::v-deep .searchable-select-popover .popover-body {
  padding: 0.5rem;
}

.popover-content {
  width: 100%;
  min-width: 200px;
}

.options-container {
  max-height: 250px;
  overflow-y: auto;
}

.option-item {
  padding: 0.5rem 1rem;
  cursor: pointer;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  border-radius: 0.25rem;
}

.option-item:hover {
  background-color: #f8f9fa;
}

.option-item.active {
  background-color: #007bff;
  color: white;
}

/* Small size */
::v-deep .btn-sm {
  padding: 0.25rem 0.5rem;
  font-size: 0.875rem;
}
</style>
